import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/AdminLayout";
import { AlertsCard } from "@/components/AlertsCard";
import { api, errMsg } from "@/lib/api";

function PasswordCard() {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", f);
      toast.success("Password changed");
      setF({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4 rounded-lg border border-border bg-card p-6">
      <h2 className="text-lg font-medium">Change your password</h2>
      <div className="space-y-1.5"><Label>Current password</Label><Input type="password" required value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="settings-current-password-input" /></div>
      <div className="space-y-1.5"><Label>New password</Label><Input type="password" required minLength={6} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="settings-new-password-input" /></div>
      <Button type="submit" data-testid="settings-change-password-button">Update password</Button>
    </form>
  );
}

export default function AdminSettings() {
  const [f, setF] = useState(null);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setF(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/admin/settings", {
        reconstitution_fee: Number(f.reconstitution_fee), bac_water_low_threshold_ml: Number(f.bac_water_low_threshold_ml),
        bitcoin_address: f.bitcoin_address, cash_instructions: f.cash_instructions,
        label_discard_days: Number(f.label_discard_days),
        notify_client_ready: f.notify_client_ready, pickup_message: f.pickup_message,
        notify_client_confirmed: f.notify_client_confirmed, confirm_message: f.confirm_message,
        referral_enabled: f.referral_enabled, referral_friend_percent: Number(f.referral_friend_percent),
        referral_referrer_percent: Number(f.referral_referrer_percent),
      });
      setF(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (!f) return null;
  return (
    <div>
      <PageHeader eyebrow="Configuration" title="Settings" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <form onSubmit={save} className="space-y-4 rounded-lg border border-border bg-card p-6">
          <h2 className="text-lg font-medium">Business settings</h2>
          <div className="space-y-1.5">
            <Label>Reconstitution fee per vial ($)</Label>
            <Input type="number" step="0.01" min="0" value={f.reconstitution_fee} onChange={(e) => setF({ ...f, reconstitution_fee: e.target.value })} className="font-mono-data" data-testid="settings-recon-fee-input" />
            <p className="text-xs text-muted-foreground">Covers BAC water and sterile syringes. Added per vial when a client asks you to reconstitute.</p>
          </div>
          <div className="space-y-1.5">
            <Label>BAC water low-stock alert (mL)</Label>
            <Input type="number" step="1" min="0" value={f.bac_water_low_threshold_ml} onChange={(e) => setF({ ...f, bac_water_low_threshold_ml: e.target.value })} className="font-mono-data" data-testid="settings-bac-threshold-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Bitcoin wallet address (shown at checkout)</Label>
            <Input value={f.bitcoin_address} onChange={(e) => setF({ ...f, bitcoin_address: e.target.value })} placeholder="Leave blank to send it after confirming" className="font-mono-data" data-testid="settings-bitcoin-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Cash payment instructions</Label>
            <Textarea value={f.cash_instructions} onChange={(e) => setF({ ...f, cash_instructions: e.target.value })} data-testid="settings-cash-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Vial labels: discard after (days)</Label>
            <Input type="number" min="0" value={f.label_discard_days} onChange={(e) => setF({ ...f, label_discard_days: e.target.value })} className="font-mono-data" data-testid="settings-discard-days-input" />
            <p className="text-xs text-muted-foreground">Printed as a discard date on each vial label. Set 0 to leave it off.</p>
          </div>
          <div className="space-y-3 rounded-md border border-border p-4">
            <label className="flex items-center justify-between text-sm font-medium">
              Email clients when their order is confirmed
              <Switch checked={f.notify_client_confirmed} onCheckedChange={(v) => setF({ ...f, notify_client_confirmed: v })} data-testid="settings-notify-confirmed-switch" />
            </label>
            <div className="space-y-1.5">
              <Label className="text-xs">Confirmation message</Label>
              <Textarea value={f.confirm_message} onChange={(e) => setF({ ...f, confirm_message: e.target.value })} data-testid="settings-confirm-message-input" />
              <p className="text-xs text-muted-foreground">Sent once per order, with the expected ready time you set on the order.</p>
            </div>
          </div>
          <div className="space-y-3 rounded-md border border-border p-4">
            <label className="flex items-center justify-between text-sm font-medium">
              Email clients when their order is ready
              <Switch checked={f.notify_client_ready} onCheckedChange={(v) => setF({ ...f, notify_client_ready: v })} data-testid="settings-notify-ready-switch" />
            </label>
            <div className="space-y-1.5">
              <Label className="text-xs">Pickup message</Label>
              <Textarea value={f.pickup_message} onChange={(e) => setF({ ...f, pickup_message: e.target.value })} data-testid="settings-pickup-message-input" />
              <p className="text-xs text-muted-foreground">Sent once per order, with the items, total and payment details if unpaid.</p>
            </div>
          </div>
          <div className="space-y-3 rounded-md border border-border p-4" data-testid="settings-referral-section">
            <label className="flex items-center justify-between text-sm font-medium">
              Referral program
              <Switch checked={f.referral_enabled} onCheckedChange={(v) => setF({ ...f, referral_enabled: v })} data-testid="settings-referral-switch" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Friend's first order (% off)</Label>
                <Input type="number" step="1" min="0" max="100" value={f.referral_friend_percent} onChange={(e) => setF({ ...f, referral_friend_percent: e.target.value })} className="font-mono-data" data-testid="settings-referral-friend-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Referrer's next order (% off)</Label>
                <Input type="number" step="1" min="0" max="100" value={f.referral_referrer_percent} onChange={(e) => setF({ ...f, referral_referrer_percent: e.target.value })} className="font-mono-data" data-testid="settings-referral-referrer-input" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Referred friends still need your approval. The referrer earns their reward once the friend's first order is marked paid. Sale items aren't discounted, and a discount code replaces these.</p>
          </div>
          <Button type="submit" data-testid="settings-save-button">Save settings</Button>
        </form>
        <PasswordCard />
        <AlertsCard />
      </div>
    </div>
  );
}
